class PattaShippingEstimator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-patta-shipping-form]');
    this.input = this.querySelector('input[name="zip"]');
    this.status = this.querySelector('[data-patta-shipping-status]');
    this.rates = this.querySelector('[data-patta-shipping-rates]');
    this.button = this.form?.querySelector('button[type="submit"]');

    if (!this.form || !this.input || this.dataset.ready === 'true') return;

    this.dataset.ready = 'true';
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      this.calculate();
    });
    this.input.addEventListener('input', () => {
      const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
      this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    });

    const savedZip = sessionStorage.getItem('patta_shipping_zip');
    if (savedZip) {
      this.input.value = savedZip;
      this.calculate();
    }

    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (this.input.value.replace(/\D/g, '').length === 8) this.calculate();
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  async quoteFromProxy(zip) {
    const origem = (this.dataset.originZip || '').replace(/\D/g, '');
    if (origem.length !== 8) {
      this.status.textContent = 'Configure o CEP de origem nas configurações do tema.';
      this.rates.replaceChildren();
      return;
    }

    this.status.textContent = 'Consultando as opções de entrega…';
    this.rates.replaceChildren();

    const payload = {
      SellerCEP: origem,
      RecipientCEP: zip,
      ShipmentInvoiceValue: Number(this.dataset.price || 0),
      ShippingServiceCode: null,
      RecipientCountry: 'BR',
      ShippingItemArray: [
        {
          Height: Number(this.dataset.pkgHeight || 5),
          Length: Number(this.dataset.pkgLength || 20),
          Width: Number(this.dataset.pkgWidth || 15),
          Weight: Math.max(Number(this.dataset.weight || 0) / 1000, 0.1),
          Quantity: 1
        }
      ]
    };

    const response = await fetch(this.dataset.quoteUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) throw new Error('Não foi possível calcular o frete agora.');

    const servicos = (data.ShippingSevicesArray || []).filter(servico => !servico.Error);
    if (!servicos.length) {
      this.status.textContent = 'Nenhuma opção de entrega para este CEP.';
      return;
    }

    sessionStorage.setItem('patta_shipping_zip', this.input.value);
    this.status.textContent = `${servicos.length} ${servicos.length === 1 ? 'opção encontrada' : 'opções encontradas'}`;

    servicos.forEach(servico => {
      const item = document.createElement('li');
      const name = document.createElement('span');
      const price = document.createElement('strong');
      const dias = Number(servico.DeliveryTime);
      const prazo = dias > 0 ? ` · até ${dias} ${dias === 1 ? 'dia útil' : 'dias úteis'}` : '';
      name.textContent = `${servico.ServiceDescription}${prazo}`;
      price.textContent = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
        Number(servico.ShippingPrice)
      );
      item.append(name, price);
      this.rates.append(item);
    });
  }

  async calculate() {
    const zip = this.input.value.replace(/\D/g, '');
    if (zip.length !== 8) {
      this.status.textContent = 'Digite um CEP válido com 8 números.';
      this.rates.replaceChildren();
      return;
    }

    this.button.disabled = true;
    try {
      if (this.dataset.context === 'product' && this.dataset.quoteUrl) {
        await this.quoteFromProxy(zip);
        return;
      }

      if (this.dataset.context === 'product') {
        const root = window.Shopify?.routes?.root || '/';
        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        const productIsInCart = cart.items?.some(item => String(item.product_id) === String(this.dataset.productId));
        if (!productIsInCart) {
          this.status.textContent = 'Adicione este produto à sacola para calcular o frete exato. Nada é adicionado escondido.';
          this.rates.replaceChildren();
          return;
        }
      }

      this.status.textContent = 'Consultando as opções para os produtos da sacola…';
      this.rates.replaceChildren();
      const addressResponse = await fetch(`https://viacep.com.br/ws/${zip}/json/`, {
        headers: { Accept: 'application/json' }
      });
      const address = await addressResponse.json();
      if (!addressResponse.ok || address.erro || !address.uf) {
        throw new Error('CEP não encontrado. Confira os números e tente novamente.');
      }

      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': address.uf
      });
      const response = await fetch(`${root}cart/shipping_rates.json?${params.toString()}`, {
        headers: { Accept: 'application/json' }
      });
      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data?.zip?.[0] || data?.province?.[0] || data?.errors || data?.error || 'Não foi possível calcular o frete para este CEP.';
        throw new Error(errorMessage);
      }

      const rates = data.shipping_rates || [];
      if (!rates.length) {
        this.status.textContent = 'Nenhuma modalidade disponível para este carrinho e CEP.';
        return;
      }

      sessionStorage.setItem('patta_shipping_zip', this.input.value);
      this.status.textContent = `${rates.length} ${rates.length === 1 ? 'opção encontrada' : 'opções encontradas'}`;
      rates.forEach(rate => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        const deliveryText = rate.delivery_days?.[1]
          ? ` · até ${rate.delivery_days[1]} ${rate.delivery_days[1] === 1 ? 'dia útil' : 'dias úteis'}`
          : '';
        name.textContent = `${rate.presentment_name || rate.name}${deliveryText}`;
        price.textContent = Number(rate.price) === 0
          ? 'Grátis'
          : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(rate.price));
        item.append(name, price);
        this.rates.append(item);
      });
    } catch (error) {
      this.status.textContent = error instanceof TypeError
        ? 'Não foi possível calcular o frete agora. Tente de novo em instantes.'
        : error.message || 'Não foi possível calcular o frete agora.';
    } finally {
      this.button.disabled = false;
    }
  }
}

if (!customElements.get('patta-shipping-estimator')) {
  customElements.define('patta-shipping-estimator', PattaShippingEstimator);
}

const compactPattaSizeGuide = () => {
  document.querySelectorAll('[id="abrir-calc-tamanho"]').forEach(button => {
    // Link discreto logo abaixo das opcoes, no estilo do "Ver avaliacoes" da
    // matriz de design: 14.3px, peso normal, caixa mista e sublinhado. O hover
    // inline do template pintava o fundo de preto, o que nao cabe num link.
    button.removeAttribute('onmouseover');
    button.removeAttribute('onmouseout');
    const compactStyles = {
      display: 'inline-flex',
      width: 'auto',
      minHeight: '24px',
      alignSelf: 'flex-start',
      justifyContent: 'flex-start',
      margin: '0',
      padding: '0',
      border: '0',
      borderRadius: '0',
      background: 'transparent',
      color: '#111',
      fontSize: '14.3px',
      fontWeight: '400',
      letterSpacing: '.05em',
      textTransform: 'none',
      textDecoration: 'underline',
      textUnderlineOffset: '3px'
    };
    Object.entries(compactStyles).forEach(([property, value]) => {
      button.style.setProperty(property.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`), value, 'important');
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', compactPattaSizeGuide, { once: true });
} else {
  compactPattaSizeGuide();
}

// Botao "Comprar agora" do tema (main-product__buy-buttons.liquid): adiciona o
// produto do formulario ao carrinho e segue direto para o checkout. Fica
// desabilitado junto com o botao de adicionar quando a variante esgota.
const syncPattaBuyNow = () => {
  document.querySelectorAll('[data-patta-buy-now]').forEach((button) => {
    if (button.getAttribute('aria-busy') === 'true') return;
    const submit = button.closest('form')?.querySelector('[name="add"]');
    if (submit && button.disabled !== submit.disabled) button.disabled = submit.disabled;
  });
};

new MutationObserver(syncPattaBuyNow).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['disabled'] });

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-patta-buy-now]');
  if (!button || button.disabled) return;
  const form = button.closest('form');
  if (!form) return;
  event.preventDefault();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const root = window.Shopify?.routes?.root || '/';
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Não foi possível adicionar o produto.');
    window.location.assign(`${root}checkout`);
  } catch (error) {
    const wrapper = form.querySelector('.product-form__error-message-wrapper');
    const message = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && message) {
      message.textContent = error.message;
      wrapper.hidden = false;
    }
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
});
